import React, { useEffect, useRef, useState } from 'react';
import CreateQuestions from '../../../component/EliteGame/CreateQuestions';
import QuestionsList from '../../../component/EliteGame/QuestionsList';
import {
  getContests,
  createQuestion,
  getQuestionsByQuizType,
  deleteQuestion,
  updateQuestion
} from '../../../api/eliteGameApi';
import { ELITE_QUIZ_TYPE } from '../../../utils/constants';
import { useEliteGames } from '../../../context/EliteGamesContext';
import { errorToast, successToast } from '../../../utils/helper';
import ViewQuestionModal from '../../../component/EliteGame/ViewQuestionModal';
import DeletePopup from '../../../component/common/modals/DeletePopup';

const ManageQuestions = () => {
  const [refresh, setRefresh] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [contestData, setContestData] = useState([]);
  const [page, setPage] = useState(1);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewQuestion, setViewQuestion] = useState(null);
  const [questionId, setQuestionId] = useState(null);
  const [open, setOpen] = useState(false);
  const { setLoading } = useEliteGames();
  const formRef = useRef(null);
  const [filter, setFilter] = useState({});
  // Function to handle viewing a question
  const handleView = (question) => {
    setViewQuestion(question);
    setViewModalOpen(true);
  };

  useEffect(() => {
    getContests()
      .then((data) => {
        setContestData(
          data.data.map((d) => ({
            ...d,
            name: d.title || d.name
          })) || []
        );
      })
      .catch((error) => {
        console.error('Failed to fetch Contest data', error);
      });
  }, []);

  const getQuestions = async () => {
    setLoading(true);
    try {
      const data = await getQuestionsByQuizType(ELITE_QUIZ_TYPE.CONTEST, {
        page,
        limit: 10,
        language: 'english',
        ...filter
      });
      setQuestions(data || []);
    } catch (error) {
      errorToast('Failed to fetch questions', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getQuestions();
  }, [refresh, page]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const formData = {
        ...data,
        quizType: ELITE_QUIZ_TYPE.CONTEST,
        options: [
          data.optionA,
          data.optionB,
          ...(data.questionType != 2 ? [data.optionC, data.optionD] : [])
        ],
        correctAnswer: data[data.answer.value || data.answer]
      };

      if (selectedQuestion) {
        const response = await updateQuestion(formData, selectedQuestion._id);
        successToast('Question updated successfully', response.message);
        setSelectedQuestion(null);
      } else {
        const response = await createQuestion(formData);
        successToast('Question created successfully', response.message);
        setRefreshFlag((prev) => prev + 1);
      }

      getQuestions(); // Refresh questions list
      setSelectedQuestion(null); // Clear selected question after submission
    } catch (error) {
      errorToast('Failed to create/update question', error);
    } finally {
      setLoading(false);
    }
    setRefresh(!refresh); // Refresh questions list after creating a new question
  };

  /**
   * coming from QuestionsList component send to create question form
   * @param {*} question
   */
  const handleEdit = (question) => {
    setSelectedQuestion(question);
    formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Handle filter action for questions
  const handleFilter = (filter) => {
    setFilter(filter);
    getQuestions();
  };

  // Handle refresh action for questions
  const handleRefresh = () => {
    setPage(1);
    getQuestions(); // Fetch fresh data
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteQuestion(questionId);
      successToast('Question deleted successfully');
      setRefresh(!refresh);
    } catch (error) {
      errorToast('Error deleting question:', error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      <div className="bg-white rounded-lg shadow p-6 mx-4">
        <div className="cursor-pointer mb-3 text-[24px] font-bold leading-9 text-shoorah-lightBlack">
          Create and Manage Questions for Contest
        </div>
      </div>
      <div ref={formRef}>
        <CreateQuestions
          onSubmit={onSubmit}
          name={'Contest'}
          quizType={ELITE_QUIZ_TYPE.CONTEST}
          dropDownData={contestData}
          existingQuestion={selectedQuestion}
          resetTrigger={refreshFlag}
        />
      </div>
      <div className="bg-white rounded-lg shadow p-6 m-4">
        <div className="mb-2">
          <span className="text-2xl font-bold text-black-600">Questions for Contest</span>
        </div>
        <hr className="mb-6" />
        <QuestionsList
          quizType={ELITE_QUIZ_TYPE.CONTEST}
          questions={questions}
          dropdowndata={contestData}
          onEdit={handleEdit}
          page={page}
          setPage={setPage}
          onView={handleView}
          onDelete={(id) => {
            setQuestionId(id);
            setOpen(true);
          }}
          onFilter={handleFilter}
          onRefresh={handleRefresh}
        />
        {DeletePopup({
          title: 'Confirm Deletion',
          open,
          setOpen,
          message:
            'Are you sure you want to delete this question? All related data will also be deleted.',
          setDelete: handleDelete
        })}
      </div>
      <ViewQuestionModal
        questions={viewQuestion}
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
      />
    </div>
  );
};

export default ManageQuestions;
